import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { CalendarMonth, fmtFull, today, DATE_TRIGGER_CLASS, QuickDateShortcuts } from './DateRangePicker';

/** Seletor de data única — mesmo visual/posicionamento do DateRangePicker, com um só mês.
 *  `className`/`children` permitem trocar o gatilho padrão (ícone + data) por um visual próprio
 *  (ex.: badge colorido de prazo) sem duplicar a lógica de popover/posicionamento. */
const DatePicker: React.FC<{
    value: string;
    onChange: (d: string) => void;
    placeholder?: string;
    align?: 'left' | 'right';
    className?: string;
    children?: React.ReactNode;
}> = ({ value, onChange, placeholder = 'Selecione a data', align = 'left', className, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() => new Date((value || today()) + 'T12:00:00').getFullYear());
    const [viewMonth, setViewMonth] = useState(() => new Date((value || today()) + 'T12:00:00').getMonth());
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    // Abre pra cima (`bottom`) por padrão, ou pra baixo (`top`) quando não há espaço acima — mesmo
    // comportamento adaptativo do DateRangePicker, ver comentário lá.
    const POPUP_ESTIMATED_HEIGHT = 380;
    const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number; rightOffset: number } | null>(null);

    useEffect(() => {
        const fn = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if (popupRef.current?.contains(target)) return;
            setIsOpen(false);
        };
        if (isOpen) document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const updateCoords = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openDownward = spaceAbove < POPUP_ESTIMATED_HEIGHT && spaceBelow > spaceAbove;
            setCoords({
                top: openDownward ? rect.bottom + 8 : undefined,
                bottom: openDownward ? undefined : window.innerHeight - rect.top + 8,
                left: rect.left,
                rightOffset: window.innerWidth - rect.right,
            });
        };
        updateCoords();
        window.addEventListener('scroll', updateCoords, true);
        window.addEventListener('resize', updateCoords);
        return () => {
            window.removeEventListener('scroll', updateCoords, true);
            window.removeEventListener('resize', updateCoords);
        };
    }, [isOpen]);

    const open = () => {
        const d = new Date((value || today()) + 'T12:00:00');
        setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
        setIsOpen(true);
    };
    const handleSelect = (dateStr: string) => { onChange(dateStr); setIsOpen(false); };
    const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
    const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

    return (
        <div className="relative">
            <button ref={triggerRef} type="button" onClick={open} className={className ?? DATE_TRIGGER_CLASS}>
                {children ?? (
                    <>
                        <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                        {value ? <span className="font-semibold">{fmtFull(value)}</span> : <span className="text-gray-400">{placeholder}</span>}
                    </>
                )}
            </button>
            {isOpen && coords && createPortal(
                <div
                    ref={popupRef}
                    style={{ position: 'fixed', ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }), ...(align === 'right' ? { right: coords.rightOffset } : { left: coords.left }) }}
                    className="z-[10050] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl"
                >
                    <QuickDateShortcuts onPick={handleSelect} />
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                        <button type="button" onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronLeft size={15} /></button>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Selecione a data</span>
                        <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronRight size={15} /></button>
                    </div>
                    <CalendarMonth year={viewYear} month={viewMonth} tempStart={value} tempEnd={value} hoverDate="" onSelect={handleSelect} onHover={() => {}} />
                    <div className="flex items-center justify-end px-4 py-2.5 border-t border-gray-100 dark:border-gray-700">
                        <button type="button" onClick={() => { onChange(''); setIsOpen(false); }} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Limpar</button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DatePicker;
